/**
 * Extras / Baggage Design Preview — mock ViewModels only.
 * Preview interaction may derive display strings. No engine/API pricing.
 */

export const EXTRAS_PREVIEW_STATES = [
  { id: "none", label: "A · No extras selected" },
  { id: "included", label: "B · Baggage included" },
  { id: "paid", label: "C · Paid baggage selected" },
  { id: "per-pax", label: "D · Different per passenger" },
  { id: "per-direction", label: "E · Outbound / return differ" },
  { id: "unavailable", label: "F · Unavailable option" },
];

const PAX = [
  { id: "pax-1", name: "David Cohen", typeLabel: "מבוגר" },
  { id: "pax-2", name: "Noa Cohen", typeLabel: "מבוגר" },
];

const DIRECTIONS = [
  { id: "outbound", label: "הלוך", routeLabel: "TLV → LCA" },
  { id: "inbound", label: "חזור", routeLabel: "LCA → TLV" },
];

const BAG_OPTIONS_PAID = [
  {
    id: "personal",
    name: "תיק אישי",
    weightLabel: "עד 5 ק״ג",
    description: "מתחת למושב",
    state: "included",
    priceUsd: 0,
    priceLabel: "כלול",
  },
  {
    id: "trolley-8",
    name: "טרולי 8 ק״ג",
    weightLabel: "8 ק״ג",
    description: "תא המטוס",
    state: "included",
    priceUsd: 0,
    priceLabel: "כלול",
  },
  {
    id: "bag-20",
    name: "מזוודה 20 ק״ג",
    weightLabel: "20 ק״ג",
    description: "כבודת בטן",
    state: "available",
    priceUsd: 35,
    priceLabel: "+$35",
  },
  {
    id: "bag-23",
    name: "מזוודה 23 ק״ג",
    weightLabel: "23 ק״ג",
    description: "כבודת בטן",
    state: "available",
    priceUsd: 49,
    priceLabel: "+$49",
  },
  {
    id: "bag-32",
    name: "מזוודה 32 ק״ג",
    weightLabel: "32 ק״ג",
    description: "כבודת בטן",
    state: "available",
    priceUsd: 72,
    priceLabel: "+$72",
  },
];

function cloneOptions(list, patchById = {}) {
  return list.map((o) => ({ ...o, ...(patchById[o.id] || {}) }));
}

/** Base extras catalog keyed by preview state id. */
export function getMockExtrasCatalog(stateId = "none") {
  const base = {
    title: "כבודה ותוספות",
    subtitle: "בחר כבודה ושירותים נוספים לכל נוסע.",
    inclusionsTitle: "מה כבר כלול בכרטיס",
    passengers: PAX,
    directions: DIRECTIONS,
    otherExtrasTitle: "שירותים נוספים",
    fare: { family: "CHARTER", cabin: "Economy" },
  };

  switch (stateId) {
    case "included":
      return {
        ...base,
        directionMode: "whole-trip",
        inclusions: [
          { id: "i1", label: "תיק אישי", tone: "included" },
          { id: "i2", label: "טרולי 8 ק״ג", tone: "included" },
          { id: "i3", label: "מזוודה 23 ק״ג", tone: "included" },
        ],
        baggageOptions: cloneOptions(BAG_OPTIONS_PAID, {
          "bag-20": { state: "unavailable", priceLabel: "לא זמין" },
          "bag-23": { state: "included", priceUsd: 0, priceLabel: "כלול" },
          "bag-32": { state: "unavailable", priceLabel: "לא זמין" },
        }),
        otherExtras: [],
        initialSelections: {},
      };

    case "paid":
      return {
        ...base,
        directionMode: "per-segment",
        inclusions: [
          { id: "i1", label: "תיק אישי", tone: "included" },
          { id: "i2", label: "טרולי 8 ק״ג", tone: "included" },
          { id: "i3", label: "מזוודה לבטן — לא כלולה", tone: "not-included" },
        ],
        baggageOptions: cloneOptions(BAG_OPTIONS_PAID),
        otherExtras: [
          {
            id: "seat",
            name: "בחירת מושב",
            description: "בחירה מראש",
            priceUsd: 12,
            priceLabel: "+$12",
            state: "available",
          },
        ],
        initialSelections: {
          "pax-1": { outbound: "bag-20", inbound: "bag-20" },
          "pax-2": { outbound: null, inbound: null },
        },
      };

    case "per-pax":
      return {
        ...base,
        directionMode: "per-segment",
        inclusions: [
          { id: "i1", label: "תיק אישי", tone: "included" },
          { id: "i2", label: "טרולי 8 ק״ג", tone: "included" },
          { id: "i3", label: "מזוודה לבטן — לא כלולה", tone: "not-included" },
        ],
        baggageOptions: cloneOptions(BAG_OPTIONS_PAID),
        otherExtras: [],
        initialSelections: {
          "pax-1": { outbound: "bag-20", inbound: "bag-20" },
          "pax-2": { outbound: "bag-23", inbound: "bag-23" },
        },
      };

    case "per-direction":
      return {
        ...base,
        directionMode: "per-segment",
        inclusions: [
          { id: "i1", label: "תיק אישי", tone: "included" },
          { id: "i2", label: "טרולי — לא כלול", tone: "not-included" },
          { id: "i3", label: "מזוודה לבטן — לא כלולה", tone: "not-included" },
        ],
        baggageOptions: cloneOptions(BAG_OPTIONS_PAID, {
          "trolley-8": {
            state: "available",
            priceUsd: 18,
            priceLabel: "+$18",
          },
        }),
        otherExtras: [],
        initialSelections: {
          "pax-1": { outbound: "bag-20", inbound: "bag-32" },
          "pax-2": { outbound: null, inbound: null },
        },
      };

    case "unavailable":
      return {
        ...base,
        directionMode: "per-segment",
        inclusions: [
          { id: "i1", label: "תיק אישי", tone: "included" },
          { id: "i2", label: "טרולי 8 ק״ג", tone: "included" },
          { id: "i3", label: "מזוודה לבטן — לא כלולה", tone: "not-included" },
        ],
        baggageOptions: cloneOptions(BAG_OPTIONS_PAID, {
          "bag-32": { state: "unavailable", priceLabel: "לא זמין" },
        }),
        otherExtras: [],
        initialSelections: {
          "pax-1": { outbound: "bag-20", inbound: null },
          "pax-2": { outbound: null, inbound: null },
        },
      };

    case "none":
    default:
      return {
        ...base,
        directionMode: "per-segment",
        inclusions: [
          { id: "i1", label: "תיק אישי", tone: "included" },
          { id: "i2", label: "טרולי 8 ק״ג", tone: "included" },
          { id: "i3", label: "מזוודה לבטן — לא כלולה", tone: "not-included" },
        ],
        baggageOptions: cloneOptions(BAG_OPTIONS_PAID),
        otherExtras: [
          {
            id: "seat",
            name: "בחירת מושב",
            description: "בחירה מראש",
            priceUsd: 12,
            priceLabel: "+$12",
            state: "available",
          },
          {
            id: "meal",
            name: "ארוחה",
            description: "תפריט בסיסי",
            priceUsd: 15,
            priceLabel: "+$15",
            state: "available",
          },
        ],
        initialSelections: {
          "pax-1": { outbound: null, inbound: null },
          "pax-2": { outbound: null, inbound: null },
        },
        initialOtherSelections: {},
      };
  }
}

const FX_RATE = 3.0987;

function formatUsd(n) {
  const v = Number(n) || 0;
  const sign = v > 0 ? "+" : "";
  return `${sign}$${v.toFixed(v % 1 ? 2 : 0)}`;
}

function formatUsdTotal(n) {
  return `$${Number(n).toFixed(2)}`;
}

function formatIls(n) {
  return `₪${Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatIlsInt(n) {
  return `₪${Math.round(n).toLocaleString("en-US")}`;
}

/**
 * Preview-only: derive StickyBookingSummary ViewModel strings from selections.
 * Not SSOT / not for production pricing.
 */
export function buildMockExtrasBookingSummary({
  baseSummary,
  catalog,
  selections,
  otherSelections = {},
}) {
  const optionsById = Object.fromEntries(
    (catalog.baggageOptions || []).map((o) => [o.id, o])
  );
  const otherById = Object.fromEntries(
    (catalog.otherExtras || []).map((o) => [o.id, o])
  );

  let bagUsd = 0;
  const bagGroups = [];
  for (const pax of catalog.passengers || []) {
    const paxSel = selections[pax.id] || {};
    const dirs =
      catalog.directionMode === "whole-trip"
        ? [{ id: "whole", label: "כל ההזמנה" }]
        : catalog.directions || [];
    const lines = [];
    for (const dir of dirs) {
      const optId = paxSel[dir.id] || null;
      if (!optId) continue;
      const opt = optionsById[optId];
      if (!opt || opt.state === "included" || !opt.priceUsd) continue;
      bagUsd += opt.priceUsd;
      lines.push({
        label: `${dir.label} · ${opt.name}`,
        amountLabel: opt.priceLabel || formatUsd(opt.priceUsd),
      });
    }
    if (lines.length) {
      bagGroups.push({
        id: pax.id,
        passengerName: pax.name,
        lines,
      });
    }
  }

  let extraUsd = 0;
  const extraItems = [];
  for (const [paxId, optIds] of Object.entries(otherSelections || {})) {
    const pax = (catalog.passengers || []).find((p) => p.id === paxId);
    for (const optId of optIds || []) {
      const opt = otherById[optId];
      if (!opt || !opt.priceUsd) continue;
      extraUsd += opt.priceUsd;
      extraItems.push({
        label: `${pax?.name?.split(" ")[0] || "נוסע"} · ${opt.name}`,
        amountLabel: opt.priceLabel,
      });
    }
  }

  const flightsUsd = 380;
  const sourceUsd = flightsUsd + bagUsd + extraUsd;

  /** Preview display fixtures for known totals; otherwise derive for mock UX only. */
  const PREBAKED = {
    380: {
      calculatedTotalLabel: "₪1,177.51",
      roundingAdjustmentLabel: "+₪0.49",
      payableTotalLabel: "₪1,178",
    },
    450: {
      calculatedTotalLabel: "₪1,394.42",
      roundingAdjustmentLabel: "+₪0.58",
      payableTotalLabel: "₪1,395",
    },
  };

  const baked = PREBAKED[sourceUsd];
  let totals;
  if (baked) {
    totals = {
      calculatedLabel: "סה״כ מחושב",
      ...baked,
      roundingLabel: "עיגול",
      payableLabel: "לתשלום",
    };
  } else {
    const calculated = sourceUsd * FX_RATE;
    const payable = Math.round(calculated);
    const rounding = payable - calculated;
    totals = {
      calculatedLabel: "סה״כ מחושב",
      calculatedTotalLabel: formatIls(calculated),
      roundingLabel: "עיגול",
      roundingAdjustmentLabel:
        Math.abs(rounding) < 0.005
          ? null
          : `${rounding >= 0 ? "+" : "-"}₪${Math.abs(rounding).toFixed(2)}`,
      payableLabel: "לתשלום",
      payableTotalLabel: formatIlsInt(payable),
    };
  }

  return {
    ...baseSummary,
    flights: {
      title: "מחיר טיסות",
      lineLabel: "2 נוסעים × $190",
      amountLabel: formatUsdTotal(flightsUsd),
    },
    baggage: {
      title: "כבודה",
      items:
        bagUsd > 0
          ? [
              {
                label: "כבודה",
                valueLabel: formatUsd(bagUsd),
                tone: "paid",
                ltr: true,
              },
            ]
          : [
              {
                label: "כבודה",
                valueLabel: "$0.00",
                tone: "muted",
                ltr: true,
              },
            ],
      extraLine: null,
      breakdown:
        bagGroups.length > 0
          ? {
              defaultExpanded: true,
              expandLabel: "פירוט לפי נוסע",
              collapseLabel: "הסתר פירוט",
              groups: bagGroups,
            }
          : null,
    },
    extras: {
      title: "תוספות",
      emptyLabel: "תוספות",
      emptyAmountLabel: "$0.00",
      items: extraItems,
    },
    fare: {
      title: "Fare",
      family: catalog.fare?.family || "CHARTER",
      cabin: catalog.fare?.cabin || "Economy",
    },
    sourceTotal: {
      title: "סה״כ במטבע העסקה",
      amountLabel: formatUsdTotal(sourceUsd),
      currency: "USD",
    },
    exchangeRate: {
      title: "שער דולר להזמנה",
      rateLabel: `$1 = ₪${FX_RATE}`,
      note: "שער ההמרה שנשמר עבור ההזמנה",
      mock: true,
    },
    totals,
    commission: baseSummary?.commission || {
      label: "עמלת סוכן (mock)",
      valueLabel: "₪94",
    },
  };
}

export function emptySelectionsForCatalog(catalog) {
  const out = {};
  for (const pax of catalog.passengers || []) {
    out[pax.id] = { ...(catalog.initialSelections?.[pax.id] || {}) };
  }
  return out;
}
